import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./GoogleSignInModal.css";

const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
const PHONE_SCOPE = "https://www.googleapis.com/auth/user.phonenumbers.read";

export default function GoogleSignInModal({
  isOpen,
  onClose,
  onSuccess,
  onError,
}) {
  const codeClientRef = useRef(null);
  const overlayRef = useRef(null);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const initGoogleClient = () => {
      if (cancelled || !window.google?.accounts?.oauth2) return;

      codeClientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope: `openid email profile ${PHONE_SCOPE}`,
        ux_mode: "popup",
        callback: async (response) => {
          if (response.error) {
            onErrorRef.current?.(
              response.error_description || "Google sign-in was cancelled.",
            );
            return;
          }

          if (response.code) {
            try {
              await onSuccessRef.current?.(response.code);
            } catch (error) {
              onErrorRef.current?.(error?.message || "Google sign-in failed.");
            }
          }
        },
      });
    };

    if (window.google?.accounts?.oauth2) {
      initGoogleClient();
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initGoogleClient;
      document.body.appendChild(script);
    }

    document.body.style.overflow = "hidden";

    return () => {
      cancelled = true;
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGoogleClick = () => {
    if (!codeClientRef.current) {
      onErrorRef.current?.("Google sign-in is still loading. Please try again.");
      return;
    }
    codeClientRef.current.requestCode();
  };

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="google-signin-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="google-signin-title"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
    >
      <div className="google-signin-panel">
        <button
          type="button"
          className="google-signin-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <h2 id="google-signin-title">Sign in to book your trial</h2>
        <p className="google-signin-subtitle">
          Continue with your Google account to confirm your free trial booking.
        </p>
        <button
          type="button"
          className="google-signin-btn"
          onClick={handleGoogleClick}
        >
          <svg viewBox="0 0 48 48" aria-hidden="true">
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </svg>
          Continue with Google
        </button>
      </div>
    </div>,
    document.body,
  );
}
